import possibleTypes from "./possibleTypes.json";
import { InMemoryCache } from "@apollo/client";

export default () =>
  new InMemoryCache({
    possibleTypes,
    typePolicies: {
      BackofficePerson: {
        fields: {
          addresses: {
            merge(_, incoming) {
              return incoming;
            }
          },
          employments: {
            merge(_, incoming) {
              return incoming;
            }
          }
        }
      },
      BackofficeCommittee: {
        fields: {
          assignments: {
            keyArgs: ["status", "personId", "search", "orderBy", "first", "last", "before", "after"],
            merge(_existing, incoming) {
              return incoming;
            }
          }
        }
      },
      BackofficeJobPost: {
        fields: {
          pointOfContact: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            }
          },
          owner: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            }
          },
          compensation: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            }
          },
          employer: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            }
          },
          jobPost: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            }
          }
        }
      },
      BackofficePersonInsights: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        }
      },
      BackofficeSection: {
        fields: {
          permissions: {
            // Unidentified nested object: header vs nav select different bits.
            // Do not also add Query.section merge — that combo stores a dangling
            // BackofficeSection ref. Identified section fields use default merge.
            merge(existing, incoming) {
              if (incoming == null) return incoming;
              return existing ? { ...existing, ...incoming } : incoming;
            }
          }
        }
      },
      Activity: {
        fields: {
          event: {
            merge(existing, incoming) {
              if (incoming == null) return incoming;
              return existing ? { ...existing, ...incoming } : incoming;
            }
          }
        }
      },
      ActivityType: {
        fields: {
          config: {
            merge(existing, incoming) {
              if (incoming == null) return incoming;
              return existing ? { ...existing, ...incoming } : incoming;
            }
          }
        }
      },
      Query: {
        fields: {
          recentlyVisited: {
            keyArgs: ["first"],
            merge(existing, incoming, { readField }) {
              if (incoming == null) return incoming;
              if (!existing) return incoming;
              const byUrl = new Map<string, (typeof incoming)[number]>();
              const takeNewer = (visit: (typeof incoming)[number]) => {
                const url = readField<string>("url", visit);
                if (!url) return;
                const prior = byUrl.get(url);
                if (!prior) {
                  byUrl.set(url, visit);
                  return;
                }
                const incomingAt = String(readField("visitedAt", visit) ?? "");
                const priorAt = String(readField("visitedAt", prior) ?? "");
                if (incomingAt >= priorAt) {
                  byUrl.set(url, visit);
                }
              };
              for (const visit of existing) takeNewer(visit);
              for (const visit of incoming) takeNewer(visit);
              return [...byUrl.values()].sort((left, right) =>
                String(readField("visitedAt", right) ?? "").localeCompare(String(readField("visitedAt", left) ?? ""))
              );
            }
          },
          event: {
            merge(existing, incoming) {
              if (!existing) return incoming;
              const merged = { ...existing, ...incoming };
              if (incoming?.activityType && existing?.activityType) {
                merged.activityType = {
                  ...existing.activityType,
                  ...incoming.activityType
                };
              }
              return merged;
            }
          }
        }
      }
    }
  });
